import React, { useEffect, useState } from 'react';
import { CreditCard, Trash2, ShieldCheck, Loader2, AlertCircle, Plus } from 'lucide-react';
import { recurringPaymentsApi } from '../../../../services/recurringPaymentsApi';
import { PaymentMethod } from '../../../../types/payments';
import { CardRegistrationForm } from './CardRegistrationForm';
import { toast } from 'sonner';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface PaymentMethodsListProps {
    /** When provided, skip initial fetch and use this list. */
    initialMethods?: PaymentMethod[];
    onDataChanged?: () => void;
    openAddFormRequest?: number;
    international?: boolean;
    phoneCountry?: string;
    /** First charge still in review: the card cannot be removed. */
    cardsLocked?: boolean;
}

export function PaymentMethodsList({
    initialMethods,
    onDataChanged,
    openAddFormRequest,
    international = false,
    phoneCountry = 'CO',
    cardsLocked = false,
}: PaymentMethodsListProps = {}) {
    const hasInitial = initialMethods !== undefined;
    const [methods, setMethods] = useState<PaymentMethod[]>(hasInitial ? initialMethods : []);
    const [loading, setLoading] = useState(!hasInitial);
    const [error, setError] = useState<string | null>(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [cardToDelete, setCardToDelete] = useState<string | null>(null);
    const [deleting, setDeleting] = useState(false);

    const loadMethods = async (force = false) => {
        setLoading(true);
        setError(null);
        try {
            const data = await recurringPaymentsApi.listCards({ force });
            setMethods(data);
        } catch (err: any) {
            console.error(err);
            setError("No pudimos cargar tus métodos de pago.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (hasInitial) {
            setMethods(initialMethods ?? []);
            setLoading(false);
            return;
        }
        loadMethods();
    }, [hasInitial, initialMethods]);

    useEffect(() => {
        if (openAddFormRequest) {
            setShowAddForm(true);
        }
    }, [openAddFormRequest]);

    const handleDelete = async () => {
        if (!cardToDelete) return;
        setDeleting(true);
        try {
            await recurringPaymentsApi.deleteCard(cardToDelete);
            toast.success("Tarjeta eliminada", {
                description: "El método de pago ha sido removido exitosamente."
            });
            if (onDataChanged) {
                onDataChanged();
            } else {
                await loadMethods(true);
            }
        } catch (err: any) {
            toast.error("Error al eliminar", {
                description: err.response?.data?.detail || "No pudimos eliminar la tarjeta. Puede que esté activa en un plan."
            });
        } finally {
            setDeleting(false);
            setCardToDelete(null);
        }
    };

    if (showAddForm) {
        return (
            <CardRegistrationForm
                international={international}
                phoneCountry={phoneCountry}
                onCancel={() => setShowAddForm(false)}
                onSuccess={() => {
                    setShowAddForm(false);
                    if (onDataChanged) {
                        onDataChanged();
                    } else {
                        loadMethods(true);
                    }
                }}
            />
        );
    }

    if (loading) {
        return (
            <div className="flex justify-center p-8">
                <Loader2 className="w-8 h-8 animate-spin text-[#1CB3B3]" />
            </div>
        );
    }

    return (
        <>
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">

                <div className="flex flex-col xl:flex-row xl:justify-between xl:items-center gap-4 mb-6 text-left">
                    <div>
                        <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                            <CreditCard className="w-5 h-5 text-[#1CB3B3]" />
                            Mis Tarjetas
                        </h3>

                        <p className="text-xs text-slate-500 mt-0.5">
                            Tarjetas guardadas para cobros automáticos
                        </p>
                    </div>

                    <button
                        onClick={() => setShowAddForm(true)}
                        className="shrink-0 bg-white hover:bg-[#E8F7F7] text-[#159A9C] px-3 py-2 rounded-lg font-semibold text-xs transition-all border border-[#CDEEEE] flex items-center gap-1 w-fit"
                    >
                        <Plus className="w-4 h-4" />
                        Nueva Tarjeta
                    </button>
                </div>

                {error ? (
                    <div className="bg-red-50 border border-red-200 p-4 rounded-xl flex items-start gap-3 text-left">
                        <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                        <p className="text-red-600 text-sm">{error}</p>
                    </div>
                ) : methods.length === 0 ? (
                    <div className="text-center py-10 bg-slate-50 rounded-xl border border-dashed border-slate-300">
                        <CreditCard className="w-10 h-10 text-slate-400 mx-auto mb-3" />

                        <p className="text-slate-500 text-sm">
                            No tienes tarjetas registradas.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-4">

                        {methods.map((method) => (
                            <div
                                key={method.id}
                                className={`flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-xl border transition-all gap-4 ${
                                    method.is_default
                                        ? 'bg-[#F3FCFA] border-[#7DE2C2] shadow-sm'
                                        : 'bg-white border-slate-200 hover:border-slate-300'
                                }`}
                            >
                                <div className="flex items-center gap-3 text-left">

                                    <div className="w-10 h-10 shrink-0 bg-white border border-slate-200 rounded-lg flex items-center justify-center p-1.5 shadow-sm">

                                        {/* Iconos por franquicia */}
                                        {method.payment_method === 'VISA' && (
                                            <span className="text-blue-600 font-bold italic text-base tracking-tighter">
                                                VISA
                                            </span>
                                        )}

                                        {method.payment_method === 'MASTERCARD' && (
                                            <div className="flex">
                                                <div className="w-3 h-3 rounded-full bg-red-500 relative left-1 z-10" />
                                                <div className="w-3 h-3 rounded-full bg-yellow-400 opacity-80" />
                                            </div>
                                        )}

                                        {method.payment_method === 'AMEX' && (
                                            <span className="text-blue-500 font-bold text-[8px] bg-blue-50 px-1 border border-blue-200 rounded">
                                                AMEX
                                            </span>
                                        )}

                                        {method.payment_method === 'DINERS' && (
                                            <span className="text-slate-700 font-serif text-xs">
                                                Diners
                                            </span>
                                        )}
                                    </div>

                                    <div className="min-w-0">
                                        <p className="text-slate-900 font-mono font-bold text-sm truncate">
                                            {method.masked_number}
                                        </p>

                                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1">
                                            <span className="text-[10px] text-slate-500 uppercase truncate max-w-[80px]">
                                                {method.card_holder_name}
                                            </span>

                                            <span className="w-0.5 h-0.5 bg-slate-400 rounded-full" />

                                            <span className="text-[10px] text-slate-500 font-mono">
                                                {method.expiration_date}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center justify-between sm:justify-end gap-2">

                                    {method.is_default && (
                                        <span className="text-[9px] bg-[#E8F7F7] text-[#159A9C] px-2 py-0.5 rounded border border-[#CDEEEE] font-bold whitespace-nowrap">
                                            PAGO PRINCIPAL
                                        </span>
                                    )}

                                    <button
                                        onClick={() => setCardToDelete(method.id)}
                                        disabled={cardsLocked}
                                        className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-40 disabled:pointer-events-none"
                                        title={cardsLocked ? 'No puedes eliminar la tarjeta mientras el pago está en revisión' : 'Eliminar tarjeta'}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        ))}

                        <div className="pt-2 flex items-center justify-center gap-1 text-[11px] text-slate-500">
                            <ShieldCheck className="w-3.5 h-3.5" />
                            Tokens generados y asegurados por ePayco
                        </div>
                    </div>
                )}

            </div>

            <AlertDialog
                open={!!cardToDelete}
                onOpenChange={(open) => !open && setCardToDelete(null)}
            >
                <AlertDialogContent className="bg-white border-slate-200 text-slate-900 shadow-xl">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-slate-900">
                            ¿Eliminar método de pago?
                        </AlertDialogTitle>

                        <AlertDialogDescription className="text-slate-500">
                            Esta acción no se puede deshacer. Si esta tarjeta está asociada a una suscripción activa, deberás asignar otra antes del próximo cobro.
                        </AlertDialogDescription>
                    </AlertDialogHeader>

                    <AlertDialogFooter>
                        <AlertDialogCancel className="bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200 hover:text-slate-900">
                            Cancelar
                        </AlertDialogCancel>

                        <AlertDialogAction
                            onClick={handleDelete}
                            disabled={deleting}
                            className="bg-red-500 hover:bg-red-600 text-white font-bold"
                        >
                            {deleting ? "Eliminando..." : "Sí, eliminar tarjeta"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
